// The student form, shared by Add Student and the Edit drawer so the two cannot drift.
import type { ReactNode } from 'react';

import { DeskInput, DeskSelect } from 'src/front-components/theme/desk-controls';
import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE, v } from 'src/front-components/theme/shell-tokens';
import {
  DESK_STATUS_LABELS,
  FOLLOW_UP_LABELS,
  INDIAN_STATES,
  SOURCE_LABELS,
  telecallerChoices,
  UNASSIGNED,
  type FormErrors,
  type RoleProfile,
  type StudentForm,
  type UserRow,
} from 'src/utils/lead-desk';

export const Field = ({
  scheme,
  label,
  required,
  error,
  children,
}: {
  scheme: string;
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) => {
  const shell = getShellColors(scheme);

  return (
    <label style={{ display: 'block', marginBottom: SHELL_SPACE[3] }}>
      <div style={{ ...SHELL_TYPE.label, color: shell.textSecondary, marginBottom: SHELL_SPACE.half }}>
        {label}
        {required === true ? ' *' : ''}
      </div>
      {children}
      {error !== undefined && <div style={{ ...SHELL_TYPE.caption, color: v('color-red'), marginTop: SHELL_SPACE.half }}>{error}</div>}
    </label>
  );
};

const TextArea = ({ scheme, value, onChange, rows = 3 }: { scheme: string; value: string; onChange: (value: string) => void; rows?: number }) => {
  const shell = getShellColors(scheme);

  return (
    <textarea
      value={value}
      rows={rows}
      onChange={(event) => onChange(event.target.value)}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: `${SHELL_SPACE[1]} ${SHELL_SPACE[2]}`,
        borderRadius: SHELL_RADIUS.sm,
        border: `1px solid ${shell.borderMedium}`,
        background: shell.background,
        color: shell.text,
        resize: 'vertical',
        ...SHELL_TYPE.body,
      }}
    />
  );
};

const pick = (labels: Record<string, string>, first: string): Array<{ value: string; label: string }> => [
  { value: '', label: first },
  ...Object.entries(labels).map(([value, label]) => ({ value, label })),
];

export const StudentFormFields = ({
  scheme,
  form,
  errors,
  onChange,
  profile,
  counsellors,
  people,
  fixedCounsellor,
  fixedTelecaller,
  roundRobin = false,
}: {
  scheme: string;
  form: StudentForm;
  errors: FormErrors;
  onChange: (patch: Partial<StudentForm>) => void;
  profile: RoleProfile;
  counsellors: string[];
  /** The people this person works with; the telecaller list follows the chosen counsellor's team. */
  people: UserRow[];
  /** A counsellor adds students to themselves, a telecaller to their own counsellor: the field is fixed and shown read-only. */
  fixedCounsellor?: string;
  fixedTelecaller?: string;
  /** Adding a student as the admin: the server picks the counsellor and telecaller in turn, so both fields are shown read-only. */
  roundRobin?: boolean;
}) => {
  const shell = getShellColors(scheme);
  const names = (list: string[], first: string): Array<{ value: string; label: string }> => [
    { value: '', label: first },
    ...list.filter((name) => name !== UNASSIGNED).map((name) => ({ value: name, label: name })),
  ];
  const counsellorLocked = !profile.canReassignCounsellor;
  const shownCounsellor = fixedCounsellor ?? form.counsellorName;
  const team = telecallerChoices(people, shownCounsellor);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', columnGap: SHELL_SPACE[6] }}>
      <div>
        <Field scheme={scheme} label="Mobile Number" required error={errors.phone}>
          <DeskInput scheme={scheme} value={form.phone} onChange={(phone) => onChange({ phone })} placeholder="e.g. 9876543210" />
        </Field>
        <Field scheme={scheme} label="Student Name" required error={errors.name}>
          <DeskInput scheme={scheme} value={form.name} onChange={(name) => onChange({ name })} />
        </Field>
        <Field scheme={scheme} label="Father/Mother Name">
          <DeskInput scheme={scheme} value={form.parentName} onChange={(parentName) => onChange({ parentName })} />
        </Field>
        <Field scheme={scheme} label="Parent Contact Number">
          <DeskInput scheme={scheme} value={form.parentPhone} onChange={(parentPhone) => onChange({ parentPhone })} placeholder="e.g. 9876543210" />
        </Field>
        <Field scheme={scheme} label="Parent Occupation">
          <DeskInput scheme={scheme} value={form.parentOccupation} onChange={(parentOccupation) => onChange({ parentOccupation })} placeholder="e.g. Business, Doctor, Agriculture" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SHELL_SPACE[2] }}>
          <Field scheme={scheme} label="Gender">
            <DeskSelect
              scheme={scheme}
              value={form.gender}
              onChange={(gender) => onChange({ gender })}
              options={[
                { value: '', label: '-- Gender --' },
                { value: 'Male', label: 'Male' },
                { value: 'Female', label: 'Female' },
                { value: 'Other', label: 'Other' },
              ]}
            />
          </Field>
          <Field scheme={scheme} label="Date of Birth">
            <DeskInput scheme={scheme} type="date" value={form.dateOfBirth} onChange={(dateOfBirth) => onChange({ dateOfBirth })} />
          </Field>
        </div>
        <Field scheme={scheme} label="Alternative Number" error={errors.alternatePhone}>
          <DeskInput scheme={scheme} value={form.alternatePhone} onChange={(alternatePhone) => onChange({ alternatePhone })} placeholder="e.g. 9876543210" />
        </Field>
        <Field scheme={scheme} label="Email ID" error={errors.email}>
          <DeskInput scheme={scheme} value={form.email} onChange={(email) => onChange({ email })} />
        </Field>
        <Field scheme={scheme} label="Town/City">
          <DeskInput scheme={scheme} value={form.city} onChange={(city) => onChange({ city })} />
        </Field>
        <Field scheme={scheme} label="State / UT">
          <DeskSelect
            scheme={scheme}
            value={form.state}
            onChange={(state) => onChange({ state })}
            options={[{ value: '', label: '-- Choose State --' }, ...INDIAN_STATES.map((s) => ({ value: s, label: s }))]}
          />
        </Field>
        <Field scheme={scheme} label="District">
          <DeskInput scheme={scheme} value={form.district} onChange={(district) => onChange({ district })} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SHELL_SPACE[2] }}>
          <Field scheme={scheme} label="Pincode">
            <DeskInput scheme={scheme} value={form.pincode} onChange={(pincode) => onChange({ pincode })} placeholder="e.g. 500034" />
          </Field>
          <Field scheme={scheme} label="Category / Quota">
            <DeskSelect
              scheme={scheme}
              value={form.category}
              onChange={(category) => onChange({ category })}
              options={[
                { value: '', label: '-- Category --' },
                { value: 'General', label: 'General' },
                { value: 'OBC', label: 'OBC' },
                { value: 'SC', label: 'SC' },
                { value: 'ST', label: 'ST' },
                { value: 'EWS', label: 'EWS' },
              ]}
            />
          </Field>
        </div>
        <Field scheme={scheme} label="Preferred Language">
          <DeskInput scheme={scheme} value={form.preferredLanguage} onChange={(preferredLanguage) => onChange({ preferredLanguage })} placeholder="e.g. Telugu, Hindi, English" />
        </Field>
        <Field scheme={scheme} label="Address">
          <TextArea scheme={scheme} value={form.address} onChange={(address) => onChange({ address })} />
        </Field>
      </div>

      <div>
        <Field scheme={scheme} label="Status">
          <DeskSelect scheme={scheme} value={form.deskStatus} onChange={(deskStatus) => onChange({ deskStatus })} options={pick(DESK_STATUS_LABELS, '-- Choose Status --')} />
        </Field>
        <Field scheme={scheme} label="Source">
          <DeskSelect scheme={scheme} value={form.leadSource} onChange={(leadSource) => onChange({ leadSource })} options={pick(SOURCE_LABELS, '-- Choose Source --')} />
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[2], marginBottom: SHELL_SPACE[3], ...SHELL_TYPE.body, color: shell.textSecondary }}>
          <input type="checkbox" checked={form.isPotential} onChange={(event) => onChange({ isPotential: event.target.checked })} />
          Potential Lead
        </label>
        <Field scheme={scheme} label="Counselor" error={errors.counsellorName}>
          {roundRobin ? (
            <DeskInput scheme={scheme} value="Assigned automatically (round robin)" onChange={() => undefined} />
          ) : counsellorLocked ? (
            <DeskInput scheme={scheme} value={shownCounsellor === '' ? UNASSIGNED : shownCounsellor} onChange={() => undefined} />
          ) : (
            // changing the counsellor clears a telecaller who does not work under the new one
            <DeskSelect
              scheme={scheme}
              value={form.counsellorName}
              onChange={(counsellorName) => onChange({ counsellorName, ...(telecallerChoices(people, counsellorName).includes(form.telecallerName) ? {} : { telecallerName: '' }) })}
              options={names(counsellors, '-- Not assigned yet --')}
            />
          )}
        </Field>
        <Field scheme={scheme} label="Telecaller">
          {roundRobin ? (
            <DeskInput scheme={scheme} value="Assigned automatically (round robin)" onChange={() => undefined} />
          ) : fixedTelecaller !== undefined ? (
            <DeskInput scheme={scheme} value={fixedTelecaller} onChange={() => undefined} />
          ) : profile.canAssignTelecaller && shownCounsellor !== '' ? (
            <DeskSelect scheme={scheme} value={form.telecallerName} onChange={(telecallerName) => onChange({ telecallerName })} options={names(team, team.length === 0 ? 'No telecallers under this counsellor' : '-- Choose Telecaller --')} />
          ) : (
            <DeskInput scheme={scheme} value={shownCounsellor === '' ? 'Assign a counsellor first' : form.telecallerName === '' ? UNASSIGNED : form.telecallerName} onChange={() => undefined} />
          )}
        </Field>
        <Field scheme={scheme} label="Next Call Date">
          <DeskInput scheme={scheme} type="datetime-local" value={form.nextCallAt} onChange={(nextCallAt) => onChange({ nextCallAt })} />
        </Field>
        <Field scheme={scheme} label="Comments">
          <TextArea scheme={scheme} value={form.notes} onChange={(notes) => onChange({ notes })} />
        </Field>
        <Field scheme={scheme} label="Quick Followup Status">
          <DeskSelect scheme={scheme} value={form.followUpStatus} onChange={(followUpStatus) => onChange({ followUpStatus })} options={pick(FOLLOW_UP_LABELS, '-- Choose Quick Followup Status --')} />
        </Field>
        <Field scheme={scheme} label="Priority">
          <DeskSelect
            scheme={scheme}
            value={form.priority || 'MEDIUM'}
            onChange={(priority) => onChange({ priority })}
            options={[
              { value: 'HIGH', label: 'High Priority' },
              { value: 'MEDIUM', label: 'Medium Priority' },
              { value: 'LOW', label: 'Low Priority' },
              { value: 'URGENT', label: 'Urgent' },
            ]}
          />
        </Field>
      </div>

      <div>
        <div style={{ ...SHELL_TYPE.pageTitle, fontSize: '15px', color: shell.text, marginBottom: SHELL_SPACE[3] }}>
          Abroad & Academic Details
        </div>
        <Field scheme={scheme} label="Course Interest">
          <DeskSelect
            scheme={scheme}
            value={form.courseInterest || 'MBBS'}
            onChange={(courseInterest) => onChange({ courseInterest })}
            options={[
              { value: 'MBBS', label: 'MBBS' },
              { value: 'BDS', label: 'BDS (Dental)' },
              { value: 'MD', label: 'MD / Post Graduate' },
              { value: 'NURSING', label: 'B.Sc Nursing' },
              { value: 'OTHER', label: 'Other Medical Program' },
            ]}
          />
        </Field>
        <Field scheme={scheme} label="Preferred Country">
          <DeskInput
            scheme={scheme}
            value={form.preferredCountry}
            onChange={(preferredCountry) => onChange({ preferredCountry })}
            placeholder="e.g. Russia, Georgia, Kyrgyzstan, Uzbekistan"
          />
        </Field>
        <Field scheme={scheme} label="Preferred / Target University">
          <DeskInput
            scheme={scheme}
            value={form.preferredUniversity}
            onChange={(preferredUniversity) => onChange({ preferredUniversity })}
            placeholder="e.g. Osh State University, Tbilisi State"
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SHELL_SPACE[2] }}>
          <Field scheme={scheme} label="NEET Score">
            <DeskInput
              scheme={scheme}
              value={form.neetScore}
              onChange={(neetScore) => onChange({ neetScore })}
              placeholder="e.g. 450"
            />
          </Field>
          <Field scheme={scheme} label="NEET Exam Year">
            <DeskInput
              scheme={scheme}
              value={form.neetYear}
              onChange={(neetYear) => onChange({ neetYear })}
              placeholder="e.g. 2026"
            />
          </Field>
        </div>
        <Field scheme={scheme} label="NEET Qualified">
          <DeskSelect
            scheme={scheme}
            value={form.neetQualified}
            onChange={(neetQualified) => onChange({ neetQualified })}
            options={[
              { value: '', label: '-- Select Qualification Status --' },
              { value: 'YES', label: 'Yes (Qualified)' },
              { value: 'NO', label: 'No (Not Qualified)' },
              { value: 'AWAITING', label: 'Awaiting Result' },
            ]}
          />
        </Field>
        <Field scheme={scheme} label="Budget Range">
          <DeskInput
            scheme={scheme}
            value={form.budget}
            onChange={(budget) => onChange({ budget })}
            placeholder="e.g. 20-25 Lakhs"
          />
        </Field>
        <Field scheme={scheme} label="Intake / Batch Year">
          <DeskInput
            scheme={scheme}
            value={form.intakeYear}
            onChange={(intakeYear) => onChange({ intakeYear })}
            placeholder="e.g. 2026"
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SHELL_SPACE[2] }}>
          <Field scheme={scheme} label="12th PCB %">
            <DeskInput
              scheme={scheme}
              value={form.twelfthPercentage}
              onChange={(twelfthPercentage) => onChange({ twelfthPercentage })}
              placeholder="e.g. 82%"
            />
          </Field>
          <Field scheme={scheme} label="10th Board %">
            <DeskInput
              scheme={scheme}
              value={form.tenthPercentage}
              onChange={(tenthPercentage) => onChange({ tenthPercentage })}
              placeholder="e.g. 88%"
            />
          </Field>
        </div>
        <Field scheme={scheme} label="Overall Education / Qualification">
          <DeskInput
            scheme={scheme}
            value={form.education}
            onChange={(education) => onChange({ education })}
            placeholder="e.g. 12th Completed, B.Sc Student"
          />
        </Field>
        <Field scheme={scheme} label="Passport Status">
          <DeskSelect
            scheme={scheme}
            value={form.passportStatus}
            onChange={(passportStatus) => onChange({ passportStatus })}
            options={[
              { value: '', label: '-- Select Passport Status --' },
              { value: 'AVAILABLE', label: 'Available (Valid)' },
              { value: 'APPLIED', label: 'Applied / In Process' },
              { value: 'NOT_AVAILABLE', label: 'Not Available' },
            ]}
          />
        </Field>
      </div>
    </div>
  );
};
